// ContributionFlow.js - Modern Stepwise Contribution Modal
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Modal, View, Text, TextInput, Pressable, Image, StyleSheet, ScrollView, Platform, Animated, Dimensions } from "react-native";
import { Ionicons } from '@expo/vector-icons';
import { useUser } from "../../Widgets/UserContext";
import { getFirestore, doc, updateDoc, collection, query, where, getDocs, arrayUnion, getDoc } from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import { LOCAL_SERVER_URL, FIREBASE_SERVER_URL } from "../../constants";
import { ECardList } from "./eCards";
import AnimatedECardPreview, { getECardAssetType, getECardEmailGifUrl, getECardMediaType, getECardPlaybackAssetUrl, normalizeAnimatedTextLayers } from "./AnimatedECardPreview";

const { width } = Dimensions.get('window');
const isMobile = width < 768;

// Import eCards data from shared list (includes storage URLs)
const staticECards = ECardList.map((c, idx) => ({ ...c, id: `static-${idx + 1}`, cardType: 'static' }));


const STEPS = [
	{ id: 1, title: 'Select Card', icon: 'gift-outline' },
	{ id: 2, title: 'Personalize', icon: 'create-outline' },
	{ id: 3, title: 'Payment', icon: 'card-outline' },
];

const ContributionFlow = ({ isVisible, onClose, pageOwner }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [selectedCard, setSelectedCard] = useState(null);
	const [personalMessage, setPersonalMessage] = useState("");
	const [senderName, setSenderName] = useState("");
	const [amount, setAmount] = useState("");
	const [guestEmail, setGuestEmail] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [successWarning, setSuccessWarning] = useState("");
	const [showSuccess, setShowSuccess] = useState(false);
	const [premiumECards, setPremiumECards] = useState([]);
	const [animatedLayerTextValues, setAnimatedLayerTextValues] = useState({});
	
	const processingFee = 5.0;
	
	const stripe = useStripe();
	const elements = useElements();
	const { currentUser, currentUserData } = useUser();
	const db = getFirestore();
	const [quickAmounts, setQuickAmounts] = useState(['25','50','100','250']);
	const eCards = useMemo(() => [...staticECards, ...premiumECards], [premiumECards]);
	const customizableAnimatedLayers = useMemo(() => {
		if (selectedCard?.cardType !== 'animated') return [];
		return normalizeAnimatedTextLayers(selectedCard).filter((layer) => layer.allowUserPersonalization);
	}, [selectedCard]);
	
	const slideAnim = useRef(new Animated.Value(0)).current;
	const fadeAnim = useRef(new Animated.Value(1)).current;

	// Load registry presets (quick amounts) from Firestore
	useEffect(() => {
		const fetchPresets = async () => {
			try {
				const configRef = doc(db, 'config', 'registry');
				const configSnap = await getDoc(configRef);
				if (configSnap.exists()) {
					const data = configSnap.data();
					const amounts = Array.isArray(data.presetAmounts) ? data.presetAmounts : [25,50,100,250];
					setQuickAmounts(amounts.map(a => String(a)));
				}
			} catch (error) {
				// ignore
			}
		};
		fetchPresets();
	}, []);

	useEffect(() => {
		if (!isVisible) return;

		const fetchPremiumECards = async () => {
			try {
				const premiumQuery = query(collection(db, "premiumECards"), where("published", "==", true));
				const snapshot = await getDocs(premiumQuery);
				const animatedCards = snapshot.docs
					.map((docSnap) => {
						const data = docSnap.data();
						const card = {
							...data,
							id: `premium-${docSnap.id}`,
							premiumECardId: docSnap.id,
							cardType: "animated",
							cardName: data.title || "Animated eCard",
							assetUrl: getECardPlaybackAssetUrl(data),
							assetType: getECardAssetType(data),
							emailGifUrl: getECardEmailGifUrl(data),
							mediaType: getECardMediaType(data),
						};
						console.log("[ContributionFlow] loaded premium eCard assets", {
							premiumECardId: docSnap.id,
							cardName: card.cardName,
							assetUrl: card.assetUrl,
							emailGifUrl: card.emailGifUrl,
							mediaType: card.mediaType,
						});
						return card;
					})
					.filter((card) => card.assetUrl)
					.sort((a, b) => {
						const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
						const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
						return bTime - aTime;
					});

				setPremiumECards(animatedCards);
			} catch (error) {
				console.warn("Unable to load premium eCards", error);
				setPremiumECards([]);
			}
		};

		fetchPremiumECards();
	}, [isVisible]);

	
	// Server URL configuration – leverage shared constants and environment.
	// NOTE: Expo web always builds with NODE_ENV='production', so we cannot
	// rely on that flag alone. Check the hostname to detect localhost / emulator.
	const isDevelopment = process.env.NODE_ENV === "development";
	const isLocalhost =
		typeof window !== 'undefined' &&
		(window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');
	const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
	// Use local Firebase Functions emulator when on localhost or in dev mode.
	const SERVER_URL = (isDevelopment || isLocalhost) ? LOCAL_SERVER_URL : FIREBASE_SERVER_URL;

	// Reset state when modal closes
	useEffect(() => {
		if (!isVisible) {
			setCurrentStep(1);
			setSelectedCard(null);
			setPersonalMessage("");
			setSenderName("");
			setAmount("");
			setGuestEmail("");
			setErrorMessage("");
			setSuccessWarning("");
			setShowSuccess(false);
			setAnimatedLayerTextValues({});
		}
	}, [isVisible]);

	// Pre-fill sender name if logged in
	useEffect(() => {
		if (currentUserData?.firstName) {
			setSenderName(`${currentUserData.firstName} ${currentUserData.lastName || ''}`);
		}
	}, [currentUserData]);

	const animateStepChange = (direction) => {
		const offset = direction === 'next' ? -50 : 50;
		
		Animated.parallel([
			Animated.timing(fadeAnim, {
				toValue: 0,
				duration: 150,
				useNativeDriver: true,
			}),
			Animated.timing(slideAnim, {
				toValue: offset,
				duration: 150,
				useNativeDriver: true,
			}),
		]).start(() => {
			slideAnim.setValue(-offset);
			Animated.parallel([
				Animated.timing(fadeAnim, {
					toValue: 1,
					duration: 200,
					useNativeDriver: true,
				}),
				Animated.timing(slideAnim, {
					toValue: 0,
					duration: 200,
					useNativeDriver: true,
				}),
			]).start();
		});
	};

	const nextStep = () => {
		if (currentStep < 3) {
			animateStepChange('next');
			setTimeout(() => setCurrentStep(prev => prev + 1), 150);
		}
	};

	const prevStep = () => {
		if (currentStep > 1) {
			animateStepChange('prev');
			setTimeout(() => setCurrentStep(prev => prev - 1), 150);
		}
	};

	const handleSelectCard = (card) => {
		setSelectedCard(card);
		if (card.cardType === "animated") {
			const initialLayerValues = {};
			normalizeAnimatedTextLayers(card)
				.filter((layer) => layer.allowUserPersonalization)
				.forEach((layer) => {
					initialLayerValues[layer.id] = layer.text;
				});
			setAnimatedLayerTextValues(initialLayerValues);
		} else {
			setAnimatedLayerTextValues({});
		}
	};

	const updateAnimatedLayerText = (layerId, value) => {
		setAnimatedLayerTextValues((current) => ({
			...current,
			[layerId]: value,
		}));
	};

	const canProceed = () => {
		switch (currentStep) {
			case 1:
				return selectedCard !== null;
			case 2:
				return senderName.trim().length > 0;
			case 3:
				const validAmount = parseFloat(amount) > 0;
				const hasEmail = currentUserData?.email || guestEmail.trim().length > 0;
				return validAmount && hasEmail;
			default:
				return false;
		}
	};

	const handlePayment = async () => {
		setErrorMessage("");
		setSuccessWarning("");
		const contributionAmount = parseFloat(amount) || 0;
		
		if (contributionAmount <= 0) {
			setErrorMessage("Please enter a valid contribution amount.");
			return;
		}

		if (!pageOwner?.stripeAccountId) {
			setErrorMessage("This page is not configured to accept contributions yet.");
			return;
		}

		setIsProcessing(true);

		try {
			const cardElement = elements.getElement(CardElement);
			if (!cardElement) throw new Error('Card element not found');

			const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
				type: 'card',
				card: cardElement,
			});

			if (createError) throw createError;


		// Enable Stripe test mode on localhost so real charges are never created during
		// local development, even if the Expo build has NODE_ENV='production'.
		const useTestMode = isDevelopment || isLocalhost || pageOwner?.useTestStripe;

		const response = await axios.post(`${SERVER_URL}/create-payment-intent`, {
			amount: Math.round((contributionAmount + processingFee) * 100),
			currency: "usd",
			sellerAccountId: pageOwner.stripeAccountId,
			isTestMode: useTestMode,
				isAdmin: currentUserData?.accountType === 'admin',
			});

			if (!response.data?.clientSecret) throw new Error("Invalid response from payment server");

			const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
				response.data.clientSecret,
				{ payment_method: paymentMethod.id }
			);

			if (confirmError) throw confirmError;

			if (paymentIntent.status === 'succeeded') {
				// Update goal tracker
				await updateGoalTracker(contributionAmount, paymentIntent);
				
				// Send eCard email
				const emailResult = await sendECardEmail(contributionAmount);
				if (!emailResult.success) {
					console.warn('eCard email was not sent:', emailResult.error);
					setSuccessWarning('Your gift was sent, but the eCard email could not be delivered automatically.');
				}
				
				setShowSuccess(true);
			}
		} catch (error) {
			
			setErrorMessage(error.message || "Payment failed. Please try again.");
		} finally {
			setIsProcessing(false);
		}
	};

	const updateGoalTracker = async (contributionAmount, paymentIntent) => {
		try {
			const usersRef = collection(db, "users");
			const q = query(usersRef, where("uid", "==", pageOwner.uid));
			const querySnapshot = await getDocs(q);
			
			if (!querySnapshot.empty) {
				const userDoc = querySnapshot.docs[0];
				const userData = userDoc.data();
				const goalTracker = userData?.pages?.registryPageConfig?.goalTracker || {};
				const currentAmount = parseFloat(goalTracker.amountContributed || 0);
				const updatedAmount = currentAmount + contributionAmount;
				const targetAmount = parseFloat(goalTracker.targetAmount || 0);
				
				// Check if goal was just reached with this contribution
				const wasGoalReached = currentAmount < targetAmount && updatedAmount >= targetAmount;
				const hasGoalReachedEmailBeenSent = goalTracker.goalReachedEmailSent || false;
				
				const contributionEntry = {
					id: `contribution-${paymentIntent?.id || Date.now()}`,
					paymentIntentId: paymentIntent?.id || null,
					amount: contributionAmount,
					contributorName: senderName,
					contributorEmail: currentUserData?.email || guestEmail || '',
					contributorPhone: currentUserData?.phone || '',
					contributorPhotoURL: currentUserData?.photoURL || currentUserData?.profilePictureURL || '',
					contributorId: currentUserData?.uid || "guest",
					message: personalMessage,
					cardName: selectedCard?.cardName || '',
					cardType: selectedCard?.cardType || 'static',
					animatedECardId: selectedCard?.premiumECardId || '',
					frontImageUrl: selectedCard?.frontStorageURL || getECardPlaybackAssetUrl(selectedCard) || '',
					eCardAssetUrl: selectedCard?.cardType === 'animated' ? getECardPlaybackAssetUrl(selectedCard) : '',
					emailGifUrl: selectedCard?.cardType === 'animated' ? getECardEmailGifUrl(selectedCard) : '',
					eCardAssetType: selectedCard?.cardType === 'animated' ? getECardAssetType(selectedCard) : '',
					eCardMediaType: selectedCard?.cardType === 'animated' ? getECardMediaType(selectedCard) : '',
					animatedTextLayers: selectedCard?.cardType === 'animated' ? normalizeAnimatedTextLayers(selectedCard) : [],
					animatedLayerTextValues: selectedCard?.cardType === 'animated' ? animatedLayerTextValues : {},
					timestamp: new Date(),
					isAnonymous: false
				};

				await updateDoc(doc(db, "users", userDoc.id), {
					"pages.registryPageConfig.goalTracker.amountContributed": updatedAmount,
					"pages.registryPageConfig.goalTracker.contributions": arrayUnion(contributionEntry),
					"pages.registryPageConfig.contributions": arrayUnion(contributionEntry)
				});
				
				// Send goal reached email if goal was just reached and email hasn't been sent
				if (wasGoalReached && !hasGoalReachedEmailBeenSent && targetAmount > 0) {
					console.log(`🎯 Goal reached! Sending goal reached email to ${pageOwner.firstName}`);
					
					try {
						// Count number of contributors
						const contributorCount = (userData?.pages?.registryPageConfig?.contributions || []).length + 1; // +1 for current contribution
						
						const emailResponse = await fetch(`${SERVER_URL}/send-goal-reached-email`, {
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({
								graduateName: `${pageOwner.firstName} ${pageOwner.lastName || ''}`.trim(),
								graduateEmail: pageOwner.email,
								goalAmount: targetAmount.toFixed(2),
								totalAmount: updatedAmount.toFixed(2),
								contributorCount: contributorCount
							})
						});

						let emailResult = null;
						try {
							emailResult = await emailResponse.json();
						} catch (e) {
							// ignore JSON parse errors
						}

						if (emailResponse.ok && emailResult && emailResult.success) {
							// Mark that goal reached email has been sent
							await updateDoc(doc(db, "users", userDoc.id), {
								"pages.registryPageConfig.goalTracker.goalReachedEmailSent": true,
								"pages.registryPageConfig.goalTracker.goalReachedDate": new Date()
							});
							console.log('✅ Goal reached email sent successfully');
						} else {
							console.error('❌ Failed to send goal reached email:', emailResponse.status, emailResult);
							// Record failure so admins can investigate and to allow retry later
							await updateDoc(doc(db, "users", userDoc.id), {
								"pages.registryPageConfig.goalTracker.goalReachedEmailFailed": true,
								"pages.registryPageConfig.goalTracker.goalReachedEmailFailedDate": new Date()
							});
						}
					} catch (emailError) {
						console.error('❌ Failed to send goal reached email:', emailError);
					}
				}
			}
		} catch (error) {
			console.error('Error updating goal tracker:', error);
		}
	};

	const sendECardEmail = async (contributionAmount) => {
		try {

			const selectedMediaType = selectedCard?.cardType === 'animated' ? getECardMediaType(selectedCard) : '';
			const selectedAssetType = selectedCard?.cardType === 'animated' ? getECardAssetType(selectedCard) : '';
			let frontUrl = selectedCard?.cardType === 'animated' ? null : selectedCard?.frontStorageURL;
			let animatedUrl = selectedCard?.cardType === 'animated' ? getECardPlaybackAssetUrl(selectedCard) : '';
			let emailGifUrl = selectedCard?.cardType === 'animated' ? getECardEmailGifUrl(selectedCard) : '';
			// Remove backUrl since it's not needed and causing 404s

			const storage = getStorage();
			const ensureToken = async (url) => {
				if (!url) return null;
				if (url.includes('token=')) return url;
				const m = url.match(/\/o\/([^?]+)/);
				if (m) {
					const path = decodeURIComponent(m[1]);
					try {
						return await getDownloadURL(storageRef(storage, path));
					} catch (err) {
						console.warn('Failed to resolve storage URL for', path, err);
					}
				}
				return url; // fallback to whatever we were given
			};

			frontUrl = await ensureToken(frontUrl);
			animatedUrl = await ensureToken(animatedUrl);
			emailGifUrl = await ensureToken(emailGifUrl);
			console.log("[ContributionFlow] sending eCard email payload assets", {
				premiumECardId: selectedCard?.premiumECardId || '',
				animatedECardUrl: animatedUrl || '',
				emailGifUrl: emailGifUrl || '',
				mediaType: selectedMediaType,
				assetType: selectedAssetType,
			});
			const animatedTextLayers = selectedCard?.cardType === 'animated'
				? normalizeAnimatedTextLayers(selectedCard)
				: [];

			const response = await fetch(`${SERVER_URL}/send-ecard-email`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					contributionAmount,
					recipientEmail: pageOwner.email,
					senderEmail: currentUserData?.email || guestEmail,
					senderName: senderName,
					name: pageOwner.firstName,
					message: personalMessage,
					frontImageUrl: frontUrl || (selectedMediaType === 'gif' ? animatedUrl : ''),
					cardType: selectedCard?.cardType || 'static',
					cardName: selectedCard?.cardName || selectedCard?.title || '',
					animatedECardUrl: selectedCard?.cardType === 'animated' ? (animatedUrl || '') : '',
					emailGifUrl,
					animatedECardId: selectedCard?.premiumECardId || '',
					assetType: selectedAssetType,
					mediaType: selectedMediaType,
					animatedTextLayers,
					animatedLayerTextValues: selectedCard?.cardType === 'animated' ? animatedLayerTextValues : {}
					// Removed backImageUrl since it's not needed
				}),
			});

			let result = null;
			try {
				result = await response.json();
			} catch (jsonError) {
				// Keep the HTTP status as the source of truth if the body is not JSON.
			}

			if (!response.ok || result?.success === false) {
				return {
					success: false,
					error: result?.error || result?.details || `eCard email request failed with status ${response.status}`
				};
			}

			return { success: true, result };
		} catch (error) {
			console.warn('sendECardEmail error', error);
			return { success: false, error: error.message || 'Unknown eCard email error' };
		}
	};

	return (
		<Modal visible={isVisible} animationType="fade" transparent={true}>
			<View style={styles.overlay}>
				<View style={styles.modalContainer}>
					{/* Header */}
					<View style={styles.header}>
						<View style={styles.headerContent}>
							{!showSuccess && (
								<View style={styles.stepIndicatorContainer}>
									{STEPS.map((step, index) => (
										<React.Fragment key={step.id}>
											<View style={styles.stepItem}>
												<View style={[
													styles.stepCircle,
													currentStep >= step.id && styles.stepCircleActive,
													currentStep === step.id && styles.stepCircleCurrent
												]}>
													{currentStep > step.id ? (
														<Ionicons name="checkmark" size={18} color="#fff" />
													) : (
														<Ionicons name={step.icon} size={18} color={currentStep >= step.id ? "#fff" : "#9ca3af"} />
													)}
												</View>
												<Text style={[
													styles.stepLabel,
													currentStep >= step.id && styles.stepLabelActive
												]}>{step.title}</Text>
											</View>
											{index < STEPS.length - 1 && (
												<View style={[
													styles.stepConnector,
													currentStep > step.id && styles.stepConnectorActive
												]} />
											)}
										</React.Fragment>
									))}
								</View>
							)}
						</View>
						<Pressable style={styles.closeButton} onPress={onClose}>
							<Ionicons name="close" size={24} color="#6b7280" />
						</Pressable>
					</View>

					{/* Content */}
					<ScrollView 
						style={styles.scrollContent}
						contentContainerStyle={styles.scrollContentContainer}
						showsVerticalScrollIndicator={false}
					>
						{showSuccess ? (
							<View style={styles.successContainer}>
								<View style={styles.successIconContainer}>
									<Ionicons name="checkmark-circle" size={80} color="#4c956c" />
								</View>
								<Text style={styles.successTitle}>Thank You!</Text>
								<Text style={styles.successMessage}>
									Your gift of ${parseFloat(amount).toFixed(2)} has been sent to {pageOwner?.firstName}!
								</Text>
								<Text style={styles.successSubtext}>
									{successWarning || 'A confirmation email has been sent to your email address.'}
								</Text>
								<Pressable style={styles.successButton} onPress={onClose}>
									<Text style={styles.successButtonText}>Done</Text>
								</Pressable>
							</View>
						) : (
							<Animated.View style={[
								styles.animatedContent,
								{
									opacity: fadeAnim,
									transform: [{ translateX: slideAnim }]
								}
							]}>
								{/* Step 1: Card Selection */}
								{currentStep === 1 && (
									<View style={styles.stepContent}>
										<Text style={styles.stepTitle}>Choose Your eCard</Text>
										<Text style={styles.stepSubtitle}>Select a beautiful card to send with your gift</Text>
										
										<View style={styles.cardsGrid}>
											{eCards.map((card) => (
												<Pressable
													key={card.id}
													onPress={() => handleSelectCard(card)}
													style={[
														styles.cardOption,
														card.cardType === 'animated' && styles.animatedCardOption,
														selectedCard?.id === card.id && styles.cardOptionSelected
													]}
												>
													{card.cardType === 'animated' ? (
														<>
															<AnimatedECardPreview
																card={card}
																style={styles.animatedCardImage}
															/>
															<View style={styles.animatedBadge}>
																<Ionicons name="play" size={12} color="#ffffff" />
																<Text style={styles.animatedBadgeText}>Animated</Text>
															</View>
														</>
													) : (
														<Image source={card.front} style={styles.cardImage} />
													)}
													{selectedCard?.id === card.id && (
														<View style={styles.selectedBadge}>
															<Ionicons name="checkmark-circle" size={28} color="#4c956c" />
														</View>
													)}
												</Pressable>
											))}
										</View>
									</View>
								)}

								{/* Step 2: Personalization */}
								{currentStep === 2 && (
									<View style={styles.stepContent}>
										<Text style={styles.stepTitle}>Personalize Your Gift</Text>
										<Text style={styles.stepSubtitle}>Add a personal touch to your contribution</Text>
										
										{selectedCard && (
											<View style={styles.selectedCardPreview}>
												{selectedCard.cardType === 'animated' ? (
													<AnimatedECardPreview
														card={selectedCard}
														layerTextValues={animatedLayerTextValues}
														style={styles.previewAnimatedCard}
													/>
												) : (
													<Image source={selectedCard.front} style={styles.previewImage} />
												)}
											</View>
										)}
										
										<View style={styles.inputGroup}>
											<Text style={styles.inputLabel}>Your Name *</Text>
											<TextInput
												style={styles.input}
												placeholder="Enter your name"
												value={senderName}
												onChangeText={setSenderName}
												placeholderTextColor="#9ca3af"
											/>
										</View>
										
										<View style={styles.inputGroup}>
											<Text style={styles.inputLabel}>Personal Message (Optional)</Text>
											<TextInput
												style={[styles.input, styles.textArea]}
												placeholder="Write a heartfelt message..."
												value={personalMessage}
												onChangeText={setPersonalMessage}
												multiline
												numberOfLines={4}
												placeholderTextColor="#9ca3af"
											/>
										</View>

										{selectedCard?.cardType === 'animated' && customizableAnimatedLayers.length > 0 && (
											<View style={styles.animatedPersonalizationSection}>
												<View style={styles.animatedPersonalizationHeader}>
													<Ionicons name="create-outline" size={18} color="#4c956c" />
													<Text style={styles.animatedPersonalizationTitle}>Personalize the animated card</Text>
												</View>
												{customizableAnimatedLayers.map((layer) => (
													<View style={styles.inputGroup} key={layer.id}>
														<Text style={styles.inputLabel}>{layer.name || "Card Text"}</Text>
														<TextInput
															style={styles.input}
															placeholder={layer.text}
															value={animatedLayerTextValues[layer.id] ?? layer.text}
															onChangeText={(value) => updateAnimatedLayerText(layer.id, value)}
															placeholderTextColor="#9ca3af"
														/>
													</View>
												))}
											</View>
										)}
									</View>
								)}

								{/* Step 3: Payment */}
								{currentStep === 3 && (
									<View style={styles.stepContent}>
										<Text style={styles.stepTitle}>Complete Your Gift</Text>
										<Text style={styles.stepSubtitle}>Enter your contribution amount and payment details</Text>
										
										{!currentUserData && (
											<View style={styles.inputGroup}>
												<Text style={styles.inputLabel}>Your Email *</Text>
												<TextInput
													style={styles.input}
													placeholder="Enter your email"
													value={guestEmail}
													onChangeText={setGuestEmail}
													keyboardType="email-address"
													placeholderTextColor="#9ca3af"
												/>
											</View>
										)}
										
										<View style={styles.inputGroup}>
											<Text style={styles.inputLabel}>Contribution Amount *</Text>
											<View style={styles.amountInputContainer}>
												<Text style={styles.currencySymbol}>$</Text>
												<TextInput
													style={styles.amountInput}
													placeholder="0.00"
													value={amount}
													onChangeText={setAmount}
													keyboardType="numeric"
													placeholderTextColor="#9ca3af"
												/>
											</View>
										</View>

										{/* Quick Amount Buttons */}
										<View style={styles.quickAmounts}>
											{quickAmounts.map((quickAmount) => (
												<Pressable
													key={quickAmount}
													style={[
														styles.quickAmountButton,
														amount === quickAmount && styles.quickAmountButtonActive
													]}
													onPress={() => setAmount(String(quickAmount))}
												>
													<Text style={[
														styles.quickAmountText,
														amount === quickAmount && styles.quickAmountTextActive
													]}>${quickAmount}</Text>
												</Pressable>
											))}
										</View>
										
										<View style={styles.summaryCard}>
											<View style={styles.summaryRow}>
												<Text style={styles.summaryLabel}>Gift Amount</Text>
												<Text style={styles.summaryValue}>${parseFloat(amount || 0).toFixed(2)}</Text>
											</View>
											<View style={styles.summaryRow}>
												<Text style={styles.summaryLabel}>Processing Fee</Text>
												<Text style={styles.summaryValue}>${processingFee.toFixed(2)}</Text>
											</View>
											<View style={styles.summaryDivider} />
											<View style={styles.summaryRow}>
												<Text style={styles.summaryTotalLabel}>Total</Text>
												<Text style={styles.summaryTotalValue}>${(parseFloat(amount || 0) + processingFee).toFixed(2)}</Text>
											</View>
										</View>
										
										<View style={styles.inputGroup}>
											<Text style={styles.inputLabel}>Card Details</Text>
											<View style={styles.stripeWrapper}>
												<CardElement options={{
													style: {
														base: {
															fontSize: '16px',
															color: '#1a3a2a',
															fontFamily: 'system-ui, sans-serif',
															'::placeholder': { color: '#9ca3af' },
														},
														invalid: { color: '#ef4444' },
													},
												}} />
											</View>
										</View>
										
										{errorMessage && (
											<View style={styles.errorBanner}>
												<Ionicons name="alert-circle" size={20} color="#ef4444" />
												<Text style={styles.errorText}>{errorMessage}</Text>
											</View>
										)}
									</View>
								)}
							</Animated.View>
						)}
					</ScrollView>

					{/* Footer Navigation */}
					{!showSuccess && (
						<View style={styles.footer}>
							{currentStep > 1 ? (
								<Pressable style={styles.backButton} onPress={prevStep}>
									<Ionicons name="arrow-back" size={20} color="#4c956c" />
									<Text style={styles.backButtonText}>Back</Text>
								</Pressable>
							) : (
								<View style={{ width: 80 }} />
							)}
							
							{currentStep < 3 ? (
								<Pressable
									style={[styles.nextButton, !canProceed() && styles.buttonDisabled]}
									onPress={nextStep}
									disabled={!canProceed()}
								>
									<Text style={styles.nextButtonText}>Continue</Text>
									<Ionicons name="arrow-forward" size={20} color="#fff" />
								</Pressable>
							) : (
								<Pressable
									style={[styles.submitButton, (!canProceed() || isProcessing) && styles.buttonDisabled]}
									onPress={handlePayment}
									disabled={!canProceed() || isProcessing}
								>
									{isProcessing ? (
										<Text style={styles.submitButtonText}>Processing...</Text>
									) : (
										<>
											<Ionicons name="gift" size={20} color="#fff" />
											<Text style={styles.submitButtonText}>Send Gift</Text>
										</>
									)}
								</Pressable>
							)}
						</View>
					)}
				</View>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.6)',
		justifyContent: 'center',
		alignItems: 'center',
		padding: isMobile ? 16 : 24,
	},
	modalContainer: {
		width: '100%',
		maxWidth: 560,
		maxHeight: '95%',
		backgroundColor: '#fefde2',
		borderRadius: 24,
		overflow: 'hidden',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 20 },
		shadowOpacity: 0.25,
		shadowRadius: 40,
		elevation: 20,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: 20,
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: 'rgba(76, 149, 108, 0.15)',
	},
	headerContent: {
		flex: 1,
	},
	closeButton: {
		width: 40,
		height: 40,
		borderRadius: 20,
		backgroundColor: '#f5f0e1',
		justifyContent: 'center',
		alignItems: 'center',
	},
	
	// Step Indicator
	stepIndicatorContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	stepItem: {
		alignItems: 'center',
	},
	stepCircle: {
		width: 36,
		height: 36,
		borderRadius: 18,
		backgroundColor: '#e5e7eb',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: 4,
	},
	stepCircleActive: {
		backgroundColor: '#4c956c',
	},
	stepCircleCurrent: {
		backgroundColor: '#4c956c',
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	stepLabel: {
		fontSize: 11,
		color: '#9ca3af',
		fontWeight: '600',
	},
	stepLabelActive: {
		color: '#4c956c',
	},
	stepConnector: {
		width: isMobile ? 30 : 50,
		height: 3,
		backgroundColor: '#e5e7eb',
		marginHorizontal: 8,
		marginBottom: 20,
		borderRadius: 2,
	},
	stepConnectorActive: {
		backgroundColor: '#4c956c',
	},
	
	// Content
	scrollContent: {
		flex: 1,
	},
	scrollContentContainer: {
		padding: 24,
	},
	animatedContent: {
		flex: 1,
	},
	stepContent: {
		flex: 1,
	},
	stepTitle: {
		fontSize: 24,
		fontWeight: '700',
		color: '#1a3a2a',
		marginBottom: 8,
		textAlign: 'center',
	},
	stepSubtitle: {
		fontSize: 15,
		color: '#6b7280',
		textAlign: 'center',
		marginBottom: 24,
	},
	
	// Cards Grid
	cardsGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'center',
		gap: 16,
	},
	cardOption: {
		width: isMobile ? '45%' : 150,
		aspectRatio: 0.7,
		borderRadius: 12,
		overflow: 'hidden',
		borderWidth: 3,
		borderColor: 'transparent',
		backgroundColor: '#fff',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.1,
		shadowRadius: 12,
		elevation: 4,
	},
	animatedCardOption: {
		aspectRatio: 1,
	},
	cardOptionSelected: {
		borderColor: '#4c956c',
	},
	cardImage: {
		width: '100%',
		height: '100%',
		resizeMode: 'cover',
	},
	animatedCardImage: {
		width: '100%',
		height: '100%',
		aspectRatio: 1,
		borderRadius: 9,
	},
	animatedBadge: {
		position: 'absolute',
		left: 8,
		bottom: 8,
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
		backgroundColor: 'rgba(26, 58, 42, 0.82)',
		borderRadius: 999,
		paddingVertical: 4,
		paddingHorizontal: 8,
	},
	animatedBadgeText: {
		color: '#ffffff',
		fontSize: 10,
		fontWeight: '700',
	},
	selectedBadge: {
		position: 'absolute',
		top: 8,
		right: 8,
		backgroundColor: '#fff',
		borderRadius: 14,
	},
	
	// Preview
	selectedCardPreview: {
		alignItems: 'center',
		marginBottom: 24,
	},
	previewImage: {
		width: 120,
		height: 170,
		borderRadius: 8,
		resizeMode: 'cover',
	},
	previewAnimatedCard: {
		width: '100%',
		maxWidth: 350,
		aspectRatio: 1,
		borderRadius: 10,
	},
	animatedPersonalizationSection: {
		backgroundColor: '#f5f0e1',
		borderWidth: 1,
		borderColor: 'rgba(76, 149, 108, 0.18)',
		borderRadius: 12,
		padding: 14,
		marginBottom: 20,
	},
	animatedPersonalizationHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		marginBottom: 12,
	},
	animatedPersonalizationTitle: {
		fontSize: 15,
		fontWeight: '700',
		color: '#1a3a2a',
	},
	
	// Inputs
	inputGroup: {
		marginBottom: 20,
	},
	inputLabel: {
		fontSize: 14,
		fontWeight: '600',
		color: '#1a3a2a',
		marginBottom: 8,
	},
	input: {
		backgroundColor: '#fff',
		borderWidth: 2,
		borderColor: 'rgba(76, 149, 108, 0.2)',
		borderRadius: 12,
		padding: 14,
		fontSize: 16,
		color: '#1a3a2a',
	},
	textArea: {
		height: 100,
		verticalAlign: 'top',
	},
	
	// Amount
	amountInputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: '#fff',
		borderWidth: 2,
		borderColor: 'rgba(76, 149, 108, 0.2)',
		borderRadius: 12,
		paddingHorizontal: 14,
	},
	currencySymbol: {
		fontSize: 24,
		fontWeight: '600',
		color: '#4c956c',
		marginRight: 4,
	},
	amountInput: {
		flex: 1,
		fontSize: 24,
		fontWeight: '600',
		color: '#1a3a2a',
		padding: 14,
	},
	quickAmounts: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 24,
		gap: 8,
	},
	quickAmountButton: {
		flex: 1,
		paddingVertical: 12,
		borderRadius: 10,
		backgroundColor: '#f5f0e1',
		alignItems: 'center',
		borderWidth: 2,
		borderColor: 'transparent',
	},
	quickAmountButtonActive: {
		backgroundColor: '#e8f0e5',
		borderColor: '#4c956c',
	},
	quickAmountText: {
		fontSize: 15,
		fontWeight: '600',
		color: '#6b7280',
	},
	quickAmountTextActive: {
		color: '#4c956c',
	},
	
	// Summary
	summaryCard: {
		backgroundColor: '#f5f0e1',
		borderRadius: 12,
		padding: 16,
		marginBottom: 24,
	},
	summaryRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		marginBottom: 8,
	},
	summaryLabel: {
		fontSize: 14,
		color: '#6b7280',
	},
	summaryValue: {
		fontSize: 14,
		color: '#1a3a2a',
		fontWeight: '500',
	},
	summaryDivider: {
		height: 1,
		backgroundColor: 'rgba(76, 149, 108, 0.2)',
		marginVertical: 8,
	},
	summaryTotalLabel: {
		fontSize: 16,
		fontWeight: '700',
		color: '#1a3a2a',
	},
	summaryTotalValue: {
		fontSize: 18,
		fontWeight: '700',
		color: '#4c956c',
	},
	
	// Stripe
	stripeWrapper: {
		backgroundColor: '#fff',
		borderWidth: 2,
		borderColor: 'rgba(76, 149, 108, 0.2)',
		borderRadius: 12,
		padding: 14,
	},
	
	// Error
	errorBanner: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: '#fef2f2',
		borderRadius: 10,
		padding: 12,
		gap: 8,
	},
	errorText: {
		flex: 1,
		fontSize: 14,
		color: '#ef4444',
	},
	
	// Footer
	footer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: 20,
		borderTopWidth: 1,
		borderTopColor: 'rgba(76, 149, 108, 0.15)',
		backgroundColor: '#fefde2',
	},
	backButton: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 4,
		padding: 12,
	},
	backButtonText: {
		fontSize: 15,
		fontWeight: '600',
		color: '#4c956c',
	},
	nextButton: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		backgroundColor: '#4c956c',
		paddingVertical: 14,
		paddingHorizontal: 24,
		borderRadius: 12,
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	nextButtonText: {
		fontSize: 16,
		fontWeight: '600',
		color: '#fff',
	},
	submitButton: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		backgroundColor: '#4c956c',
		paddingVertical: 14,
		paddingHorizontal: 24,
		borderRadius: 12,
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	submitButtonText: {
		fontSize: 16,
		fontWeight: '600',
		color: '#fff',
	},
	buttonDisabled: {
		backgroundColor: '#9ca3af',
		shadowColor: '#9ca3af',
	},
	
	// Success
	successContainer: {
		alignItems: 'center',
		paddingVertical: 40,
	},
	successIconContainer: {
		marginBottom: 20,
	},
	successTitle: {
		fontSize: 28,
		fontWeight: '700',
		color: '#4c956c',
		marginBottom: 12,
	},
	successMessage: {
		fontSize: 16,
		color: '#1a3a2a',
		textAlign: 'center',
		marginBottom: 8,
	},
	successSubtext: {
		fontSize: 14,
		color: '#6b7280',
		textAlign: 'center',
		marginBottom: 32,
	},
	successButton: {
		backgroundColor: '#4c956c',
		paddingVertical: 14,
		paddingHorizontal: 48,
		borderRadius: 12,
		shadowColor: '#4c956c',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 4,
	},
	successButtonText: {
		fontSize: 16,
		fontWeight: '600',
		color: '#fff',
	},
});

export default ContributionFlow;
